/* Rozvaha klienta — co má, co dluží, co mu zbývá.
 *
 * PROČ TO EXISTUJE (zadání majitele 10. 8. 2026):
 * „Když zadám do analýzy majetek jako dům, auto, zlato, finanční i nefinanční,
 *  musí to být někde ve smlouvě a v majetku. Graf nebo tak něco ukazuje jeho
 *  majetek."
 *
 * Klient má svoje jmění rozházené ve třech evidencích — smlouvy (stavebko,
 * penzijko, investice, KŽP, účty), nefinanční majetek (dům, auto, zlato) a
 * závazky (hypotéka, úvěry). Nikde to nebylo vidět dohromady.
 *
 * ⚠️ STEJNÁ DEFINICE JAKO lib/bilance.js. Tam už čisté jmění počítáme
 * z analýzy (`finMajetek + nefin - dluhy`). Tenhle modul počítá TOTÉŽ, jen
 * ze složky klienta (smlouvy + majetek z API místo dat analýzy). Kdyby se
 * definice rozešly, ukazovali bychom klientovi dvě různá čisté jmění na dvou
 * obrazovkách — proto je vzorec doslova stejný a hlídá to test.
 *
 * Modul je čistý (bez Reactu a bez API), aby šel testovat samostatně.
 */

/* BUDOUCÍ MAJETEK (dědictví, dar) — klient ho DNES nemá. Analýza ho propisuje
 * do složky jako `type: "budouci"`, `kind: "dedictvi"` (lib/analyzaV2/
 * propsaniMajetku.js → nefinancni.js BUDOUCI / DRUH_DEDICTVI). Stejně jako
 * lib/bilance.js (29. 9. 2026) se NEsčítá do nefinančního majetku ani do
 * čistého jmění a jde ven zvlášť jako `budouci`. Dřív ho složka přičítala:
 * byt 5 mil. + dědictví 3 mil. = čisté jmění 8 mil. ve složce, ale 5 mil.
 * v analýze — dvě různá čísla u téhož klienta. */
const TYP_BUDOUCI = "budouci";
const DRUH_DEDICTVI = "dedictvi";
const jeBudouci = (m) =>
  String(m?.type ?? "").trim().toLowerCase() === TYP_BUDOUCI
  || String(m?.kind ?? "").trim().toLowerCase() === DRUH_DEDICTVI;

/* Typy smluv, které jsou DLUH. Jediný seznam, na kterém rozvaha stojí. */
const DLUHOVE_TYPY = ["hypoteka", "uver", "uver_osobni", "uver_firemni", "leasing"];

/* ⚠️ MAJETEK SE NEPOZNÁVÁ PODLE NÁZVU TYPU, ALE PODLE TOHO, JESTLI SMLOUVA
 * NESE HODNOTU. Původně tu byl seznam „spořicích typů" a málem to prošlo
 * s tichou chybou:
 *   - analýza propisuje stavební spoření jako `stavebni`, ne `stavebko`
 *     (lib/analyzaV2/propsaniSmluv.js: produkt("stavebko", "stavebni")),
 *     takže by v rozvaze CHYBĚLO,
 *   - kapitálové životní jde jako `pojisteni_zivot` — což vypadá jako riziková
 *     pojistka, ale klient v něm peníze MÁ (odkupné).
 * Seznam názvů by se rozešel při každé změně v propisování. Hodnota je
 * spolehlivější znak: riziková pojistka žádnou nemá, spořicí produkt ano. */

/** Číslo, nebo null. NIKDY 0 z prázdné hodnoty — „neznáme" není „nula". */
export function cislo(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

const jeTyp = (t, seznam) => seznam.includes(String(t ?? "").trim().toLowerCase());

/* Z čeho číst hodnotu smlouvy — pole se historicky jmenují různě.
 *
 * ⚠️ NÁLEZ 17. 8. 2026: seznam neobsahoval `total_amount` — a přesně to pole
 * do složky zapisuje analýza (lib/analyzaV2/propsaniSmluv.js staví payload
 * s `total_amount: num(r.hodnota)`). Rozvaha proto byla SLEPÁ ke všemu, co
 * analýza propsala: klient se stavebkem 200 000, penzijkem 500 000, investicí
 * 1 000 000 a hypotékou 4 000 000 měl ve složce finanční majetek 0 a dluhy 0.
 * Komentář nad tímhle blokem přitom tenhle druh chyby výslovně řeší — jen si
 * vybral špatná pole.
 *
 * POŘADÍ JE VÝZNAM, ne libovůle:
 *   • `current_value` / `saved_amount` jsou PRŮBĚŽNÝ STAV, který poradce
 *     dopsal ve složce ručně (ContractEditor). Je čerstvější než analýza,
 *     proto má přednost.
 *   • `remaining_amount` je u úvěru „kolik zbývá splatit" — u dluhu je to
 *     pravdivější číslo než původní jistina, proto se u dluhů čte dřív než
 *     `total_amount`.
 *   • `total_amount` je až poslední: u spořicího produktu je to hodnota
 *     z analýzy, u úvěru původní jistina.
 * Kdyby se pořadí obrátilo, ruční oprava poradce ve složce by se přestala
 * projevovat a rozvaha by donekonečna ukazovala čísla ze staré analýzy. */
const POLE_HODNOTY = ["current_value", "saved_amount", "hodnota", "value", "balance", "total_amount", "principal"];
const POLE_DLUHU = ["remaining_amount", "current_value", "hodnota", "value", "balance", "total_amount", "principal"];

function hodnotaSmlouvy(s, dluh = false) {
  for (const k of (dluh ? POLE_DLUHU : POLE_HODNOTY)) {
    const v = cislo(s?.[k]);
    if (v !== null) return v;
  }
  return null;
}

/* ══════════════════════════════════════════════════════════════════════
 * BANKOVNÍ ÚČTY SE ČTOU Z ANALÝZY (audit 3. 10. 2026, nález A3.3)
 *
 * Analýza do složky propisuje smlouvy (stavebko, penzijko, OPF, KŽP, úvěry,
 * pojistky) a nefinanční majetek — ale BANKOVNÍ ÚČTY ne: smlouva typu „účet“
 * ve složce neexistuje (backend models_contracts.ContractType ho nezná).
 * Karta klienta tak počítala čisté jmění bez peněz na účtech:
 *   demo 2 971 000 proti 3 196 000 v analýze, Novákovi 2 760 000 proti
 *   2 910 000, SK rodina 70 000 € proti 79 000 €. Klientka, která měla
 *   peníze JEN na účtech, neměla na kartě rozvahu vůbec.
 *
 * PROČ SE ÚČTY NEPROPISUJÍ DO SLOŽKY JAKO SMLOUVY: musely by jít jako typ
 * „jine“ a automatický propis (běží každých ~12 s) by je založil u každého
 * klienta s analýzou — do Smluv, servisního radaru i hledání úniků. Účty
 * klienta poradce ve složce nespravuje, takže se jen DOČÍTAJÍ z analýzy.
 *
 * ⚠️ Kdyby se jednou účty do složky propisovat začaly, tenhle vstup se MUSÍ
 * zrušit — jinak se sečtou dvakrát. Hlídá to test „karta = analýza“
 * (lib/crm/__tests__/kartaRovnaSeAnalyze.test.js).
 *
 * Vstupem jsou data analýzy PŘIPRAVENÁ jako na stránce analýzy
 * (lib/analyzaV2/menaDat.js → pripravAnalyzu): řádek v cizí měně se starým
 * nebo chybějícím kurzem tam má hodnotu 0, takže se nezapočítá ani tady.
 * ══════════════════════════════════════════════════════════════════════ */

/**
 * Účty a hotovost z analýzy ve tvaru položek rozvahy.
 * @param {object} d  připravená data analýzy (v2)
 * @returns {Array<{nazev:string, castka:number, typ:"ucet"}>}
 */
export function uctyZAnalyzy(d) {
  const ucty = d && typeof d === "object" && d.majetek && Array.isArray(d.majetek.ucty) ? d.majetek.ucty : [];
  const out = [];
  for (const r of ucty) {
    const v = cislo(r?.hodnota);
    /* Nula i prázdno se přeskočí — stejně jako nedluhová smlouva bez hodnoty.
       Bilance analýzy je sčítá jako 0, takže součty sedí i tak. */
    if (v === null || v <= 0) continue;
    out.push({ nazev: String(r?.oznaceni ?? "").trim() || "Účet", castka: v, typ: "ucet" });
  }
  return out;
}

/**
 * Sestaví rozvahu ze smluv a nefinančního majetku.
 *
 * @param {Array} smlouvy  z /api/contracts
 * @param {Array} majetek  z /api/assets
 * @param {object} [zAnalyzy]  { ucty } — účty z analýzy (uctyZAnalyzy), viz výš
 * @returns rozpis + součty + KOLIK POLOŽEK NEMÁ HODNOTU
 *
 * Položky bez hodnoty se do součtu NEZAPOČÍTÁVAJÍ a výsledek to přizná.
 * Tiché sečtení neznámé hodnoty jako nuly by klientovi ukázalo menší jmění,
 * než jaké má — a poradce by o tom nevěděl.
 */
export function rozvaha(smlouvy = [], majetek = [], zAnalyzy = {}) {
  const sml = Array.isArray(smlouvy) ? smlouvy : [];
  const maj = Array.isArray(majetek) ? majetek : [];
  const ucty = Array.isArray(zAnalyzy?.ucty) ? zAnalyzy.ucty : [];

  const polozky = { financni: [], nefinancni: [], dluhy: [], budouci: [] };
  let bezHodnoty = 0;

  for (const s of sml) {
    const typ = s?.type;
    const dluh = jeTyp(typ, DLUHOVE_TYPY);
    const v = hodnotaSmlouvy(s, dluh);
    if (dluh) {
      if (v === null) { bezHodnoty++; continue; }
      /* Dluh držíme jako KLADNÉ číslo a odečítáme až v součtu — jinak by
         se znaménko dvakrát otočilo podle toho, jak ho kdo uložil. */
      polozky.dluhy.push({ nazev: s?.provider || s?.name || "Úvěr", castka: Math.abs(v), typ });
      continue;
    }
    /* Nedluhová smlouva s KLADNOU hodnotou = klientovy peníze uvnitř.
       Nula i chybějící hodnota se přeskočí a NEPOČÍTÁ se mezi neoceněné —
       riziková pojistka hodnotu prostě nemá a upozorňovat na to by mátlo. */
    if (v !== null && v > 0) {
      polozky.financni.push({ nazev: s?.provider || s?.name || "Smlouva", castka: v, typ });
    }
  }

  /* Účty z analýzy jsou FINANČNÍ majetek — stejně jako v lib/bilance.js
     (finMajetek = účty + OPF + stavebko + penzijko + KŽP). */
  for (const u of ucty) {
    const v = cislo(u?.castka);
    if (v === null || v <= 0) continue;
    polozky.financni.push({ nazev: u?.nazev || "Účet", castka: v, typ: u?.typ || "ucet" });
  }

  for (const m of maj) {
    const v = cislo(m?.value);
    const budouci = jeBudouci(m);
    /* Neoceněné dědictví nehlásíme jako „položku bez hodnoty“ — do dnešního
       jmění by nešlo ani s cenou, takže neúplnost dnešní rozvahy nezpůsobuje. */
    if (v === null) { if (!budouci) bezHodnoty++; continue; }
    const polozka = { nazev: m?.nazev || m?.name || "Majetek", castka: v, druh: m?.kind || "ostatni" };
    (budouci ? polozky.budouci : polozky.nefinancni).push(polozka);
  }

  const sect = (a) => a.reduce((s, x) => s + x.castka, 0);
  const financni = sect(polozky.financni);
  const nefinancni = sect(polozky.nefinancni);
  const dluhy = sect(polozky.dluhy);
  const budouci = sect(polozky.budouci);

  /* Stejný vzorec jako lib/bilance.js (computeBilanceCore):
   *   cisteJmeni = finMajetek + nefin − dluhy, nefin = nemovitosti + firma BEZ dědictví,
   *   budoucí majetek (dědictví) zvlášť jako `budouci` — do jmění ani do majetku celkem nejde. */
  const cisteJmeni = financni + nefinancni - dluhy;
  const majetekCelkem = financni + nefinancni;

  return {
    financni, nefinancni, dluhy, majetekCelkem, cisteJmeni, budouci,
    polozky,
    bezHodnoty,
    /* Prázdná rozvaha NENÍ „nulové jmění" — je to „nic nevíme". Obrazovka
       podle toho pozná, jestli má ukázat graf, nebo prázdný stav. */
    prazdna: polozky.financni.length === 0 && polozky.nefinancni.length === 0 && polozky.dluhy.length === 0,
    /* (Samotné dědictví rozvahu „neplní“ — o dnešku pořád nic nevíme.) */
  };
}

/** Rozpad nefinančního majetku po druzích — pro graf. */
export function podleDruhu(r) {
  const m = {};
  for (const p of r?.polozky?.nefinancni || []) {
    m[p.druh] = (m[p.druh] || 0) + p.castka;
  }
  return Object.entries(m)
    .map(([druh, castka]) => ({ druh, castka }))
    .sort((a, b) => b.castka - a.castka);
}

/**
 * Podíl na celku v procentech. Když je celek nula, vrací null —
 * dělení nulou by dalo Infinity a v grafu pruh přes celou obrazovku.
 */
export function podil(cast, celek) {
  if (!Number.isFinite(cast) || !Number.isFinite(celek) || celek <= 0) return null;
  return (cast / celek) * 100;
}
